import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { shuffle } from 'lodash';
import { firestore } from '../firebaseConfig';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import HeroSection from './HeroSection';

import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';

const BATCH     = 24;
const CACHE_KEY = 'vedo_dominios_cache_v2';
const CACHE_TTL = 60 * 60 * 1000;

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

  :root {
    --bg:           #0a1628;
    --bg-card:      #0f1e35;
    --bg-card-h:    #132440;
    --teal:         #00adb5;
    --teal-dim:     rgba(0,173,181,0.12);
    --teal-border:  rgba(0,173,181,0.22);
    --accent:       #ff5722;
    --accent-h:     #e64a19;
    --white:        #f0f4f8;
    --muted:        rgba(240,244,248,0.45);
    --transition:   0.22s cubic-bezier(0.4,0,0.2,1);
    --filter-w:    240px;
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    background: linear-gradient(150deg, #0d2240 0%, #0a1628 40%, #080f1c 100%);
    background-attachment: fixed;
    color: var(--white);
    font-family: 'DM Sans', sans-serif;
    min-height: 100vh;
  }

  .vedo-home {
    display: flex;
    align-items: flex-start;
    min-height: calc(100vh - 100px);
  }

  /* ── Overlay mobile ── */
  .vedo-overlay {
    display: none;
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.55);
    z-index: 200;
    backdrop-filter: blur(2px);
  }
  .vedo-overlay.active { display: block; }

  /* ── Filter sidebar ── */
  .vedo-filter {
    width: var(--filter-w);
    flex-shrink: 0;
    flex-grow: 0;
    background: #0c1a2e;
    border-right: 1px solid var(--teal-border);
    padding: 24px 0;
    position: sticky;
    top: 100px;
    height: calc(100vh - 100px);
    overflow-y: auto;
    transition: transform var(--transition);
    z-index: 10;
  }
  .vedo-filter::-webkit-scrollbar { width: 4px; }
  .vedo-filter::-webkit-scrollbar-track { background: transparent; }
  .vedo-filter::-webkit-scrollbar-thumb { background: var(--teal-border); border-radius: 4px; }

  @media (max-width: 768px) {
    .vedo-filter {
      position: fixed;
      top: 100px;
      left: 0;
      bottom: 0;
      height: calc(100vh - 100px);
      z-index: 300;
      transform: translateX(-100%);
      width: 85%;
      max-width: 320px;
      overflow-y: auto;
    }
    .vedo-filter.open {
      transform: translateX(0);
      box-shadow: 4px 0 32px rgba(0,0,0,0.5);
    }
  }

  .vedo-filter__header {
    padding: 0 20px 16px;
    border-bottom: 1px solid var(--teal-border);
    margin-bottom: 8px;
  }
  .vedo-filter__title {
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--teal);
    margin-bottom: 12px;
  }
  .vedo-filter__search {
    width: 100%;
    background: var(--teal-dim);
    border: 1px solid var(--teal-border);
    border-radius: 6px;
    padding: 9px 12px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    color: var(--white);
    outline: none;
    transition: border-color var(--transition), background var(--transition);
  }
  .vedo-filter__search::placeholder { color: var(--muted); }
  .vedo-filter__search:focus {
    border-color: var(--teal);
    background: rgba(0,173,181,0.16);
  }

  .vedo-filter__setores { padding: 8px 0; }

  .vedo-filter__all {
    display: flex;
    align-items: center;
    padding: 9px 20px;
    font-size: 13px;
    color: var(--muted);
    cursor: pointer;
    transition: color var(--transition), background var(--transition);
    border-left: 2px solid transparent;
  }
  .vedo-filter__all:hover,
  .vedo-filter__all.active {
    color: var(--white);
    background: var(--teal-dim);
    border-left-color: var(--teal);
  }

  .vedo-filter__setor {
    display: flex;
    align-items: center;
    padding: 9px 20px;
    font-size: 13px;
    color: var(--muted);
    cursor: pointer;
    transition: color var(--transition), background var(--transition);
    border-left: 2px solid transparent;
  }
  .vedo-filter__setor:hover,
  .vedo-filter__setor.active {
    color: var(--white);
    background: var(--teal-dim);
    border-left-color: var(--teal);
  }
  .vedo-filter__setor-name {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* ── Main ── */
  .vedo-main {
    flex: 1;
    min-width: 0;
    max-width: 100%;
    padding: 24px 20px;
    overflow: hidden;
  }

  /* ── Toolbar ── */
  .vedo-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
  }
  .vedo-toolbar__filter-btn {
    display: none;
    align-items: center;
    gap: 6px;
    background: var(--teal-dim);
    border: 1px solid var(--teal-border);
    border-radius: 6px;
    padding: 9px 14px;
    color: var(--white);
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    cursor: pointer;
    white-space: nowrap;
    transition: background var(--transition);
    flex-shrink: 0;
  }
  .vedo-toolbar__filter-btn:hover { background: rgba(0,173,181,0.2); }
  @media (max-width: 768px) {
    .vedo-toolbar__filter-btn { display: flex; }
  }
  .vedo-toolbar__info {
    flex: 1;
    font-size: 12px;
    color: var(--muted);
    letter-spacing: 0.05em;
  }
  .vedo-toolbar__setor-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    background: var(--teal-dim);
    border: 1px solid var(--teal-border);
    border-radius: 20px;
    padding: 5px 12px;
    font-size: 12px;
    color: var(--teal);
    cursor: pointer;
    transition: background var(--transition);
  }
  .vedo-toolbar__setor-badge:hover { background: rgba(0,173,181,0.2); }
  .vedo-toolbar__setor-badge svg { width: 12px; height: 12px; opacity: 0.7; }

  /* ── Grid ── */
  .vedo-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
  }
  @media (min-width: 480px) {
    .vedo-grid { grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 14px; }
  }
  @media (min-width: 1024px) {
    .vedo-grid { grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 16px; }
  }

  /* ── Card ── */
  .vedo-card {
    background: var(--bg-card);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 10px;
    padding: 18px 16px 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    transition: background var(--transition), border-color var(--transition), transform var(--transition), box-shadow var(--transition);
    animation: cardIn 0.35s ease both;
    position: relative;
    overflow: hidden;
  }
  @keyframes cardIn {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .vedo-card:hover {
    background: var(--bg-card-h);
    border-color: var(--teal-border);
    transform: translateY(-2px);
    box-shadow: 0 6px 24px rgba(0,173,181,0.1);
  }

  .vedo-card--sold {
    opacity: 0.6;
    border-color: rgba(255,255,255,0.03);
  }
  .vedo-card--sold:hover {
    transform: none;
    box-shadow: none;
    border-color: rgba(255,255,255,0.06);
  }

  .vedo-card__sold-badge {
    position: absolute;
    top: 12px;
    right: -22px;
    background: rgba(120,120,130,0.55);
    color: rgba(240,244,248,0.7);
    font-size: 8.5px;
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    padding: 3px 28px;
    transform: rotate(35deg);
    pointer-events: none;
    backdrop-filter: blur(4px);
  }

  .vedo-card__sold-badge.reserved {
    background: rgba(0,173,181,0.3);
    color: white;
  }

  .vedo-card__domain {
    font-family: 'DM Mono', monospace;
    font-size: 13.5px;
    font-weight: 500;
    color: var(--white);
    letter-spacing: 0.01em;
    word-break: break-all;
    line-height: 1.4;
  }
  .vedo-card__setor {
    font-size: 11px;
    font-weight: 400;
    color: var(--teal);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    opacity: 0.8;
  }

  .vedo-card__price-wrap {
    margin-top: 2px;
    min-height: 24px;
    display: flex;
    align-items: center;
  }
  .vedo-card__price-toggle {
    display: flex;
    align-items: center;
    gap: 5px;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: var(--muted);
    font-family: 'DM Sans', sans-serif;
    font-size: 11.5px;
    letter-spacing: 0.06em;
    transition: color var(--transition);
  }
  .vedo-card__price-toggle:hover { color: var(--teal); }
  .vedo-card__price-toggle svg { flex-shrink: 0; transition: transform var(--transition); }
  .vedo-card__price-toggle:hover svg { transform: scale(1.15); }

  .vedo-card__price {
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: var(--white);
    letter-spacing: 0.01em;
    animation: priceReveal 0.3s cubic-bezier(0.4,0,0.2,1) both;
    display: flex;
    align-items: baseline;
    gap: 3px;
  }
  .vedo-card__price span {
    font-size: 11px;
    font-weight: 400;
    color: var(--muted);
  }
  @keyframes priceReveal {
    from { opacity: 0; transform: translateY(4px); filter: blur(4px); }
    to   { opacity: 1; transform: translateY(0);   filter: blur(0); }
  }

  .vedo-card__spacer { flex: 1; }

  .vedo-card__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid var(--teal-border);
    border-radius: 6px;
    color: var(--teal);
    font-family: 'DM Sans', sans-serif;
    font-size: 12.5px;
    font-weight: 500;
    padding: 7px 12px;
    cursor: pointer;
    letter-spacing: 0.04em;
    transition: background var(--transition), border-color var(--transition), color var(--transition);
    width: 100%;
  }
  .vedo-card__btn:hover {
    background: var(--accent);
    border-color: var(--accent);
    color: #fff;
    box-shadow: 0 3px 12px rgba(255,87,34,0.35);
  }
  .vedo-card__btn--sold {
    border-color: rgba(255,255,255,0.08);
    color: rgba(240,244,248,0.25);
    cursor: not-allowed;
    pointer-events: none;
  }

  /* ── Spinner ── */
  .vedo-spinner-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
    padding: 80px 20px;
    width: 100%;
  }
  .vedo-spinner { position: relative; width: 64px; height: 64px; }
  .vedo-spinner__ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 2px solid transparent;
    border-top-color: var(--teal);
    animation: spinRing 1.1s cubic-bezier(0.4,0,0.2,1) infinite;
  }
  .vedo-spinner__ring--mid {
    inset: 10px;
    border-top-color: transparent;
    border-right-color: rgba(0,173,181,0.5);
    animation-duration: 0.8s;
    animation-direction: reverse;
  }
  .vedo-spinner__ring--inner {
    inset: 20px;
    border-top-color: var(--accent);
    animation-duration: 1.4s;
  }
  .vedo-spinner__dot {
    position: absolute;
    top: 50%; left: 50%;
    width: 6px; height: 6px;
    border-radius: 50%;
    background: var(--teal);
    transform: translate(-50%, -50%);
    animation: pulseDot 1.1s ease-in-out infinite;
  }
  @keyframes spinRing { to { transform: rotate(360deg); } }
  @keyframes pulseDot {
    0%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
    50%       { opacity: 0.4; transform: translate(-50%, -50%) scale(0.5); }
  }
  .vedo-spinner__label {
    font-size: 12px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--muted);
    animation: fadeLabel 1.6s ease-in-out infinite;
  }
  @keyframes fadeLabel {
    0%, 100% { opacity: 0.45; }
    50%       { opacity: 0.9; }
  }

  /* ── Infinite scroll ── */
  .vedo-sentinel { height: 1px; width: 100%; }
  .vedo-load-more {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px 0 16px;
    gap: 10px;
    color: var(--muted);
    font-size: 12px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
  .vedo-load-more__ring {
    width: 18px; height: 18px;
    border-radius: 50%;
    border: 1.5px solid transparent;
    border-top-color: var(--teal);
    animation: spinRing 0.8s linear infinite;
    flex-shrink: 0;
  }

  /* ── Empty / Fim ── */
  .vedo-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 80px 20px;
    color: var(--muted);
    font-size: 14px;
    text-align: center;
  }
  .vedo-empty__icon { font-size: 36px; opacity: 0.3; }
  .vedo-end {
    text-align: center;
    padding: 32px 0 8px;
    font-size: 11px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: rgba(240,244,248,0.15);
  }

  /* ── Modal Auth ── */
  .vedo-auth-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.7);
    backdrop-filter: blur(6px);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: fadeIn 0.2s ease;
  }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

  .vedo-auth-modal {
    background: #0f1e35;
    border: 1px solid var(--teal-border);
    border-radius: 14px;
    padding: 36px 32px 28px;
    width: 100%;
    max-width: 380px;
    box-shadow: 0 24px 64px rgba(0,0,0,0.6);
    animation: modalIn 0.25s cubic-bezier(0.4,0,0.2,1);
    position: relative;
  }
  @keyframes modalIn {
    from { opacity: 0; transform: translateY(16px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .vedo-auth-modal__close {
    position: absolute;
    top: 14px; right: 16px;
    background: none; border: none;
    color: var(--muted); cursor: pointer;
    font-size: 20px; line-height: 1; padding: 4px;
    transition: color var(--transition);
  }
  .vedo-auth-modal__close:hover { color: var(--white); }
  .vedo-auth-modal__logo { text-align: center; margin-bottom: 6px; }
  .vedo-auth-modal__logo span {
    font-family: 'DM Mono', monospace;
    font-size: 11px; letter-spacing: 0.25em;
    text-transform: uppercase; color: var(--teal);
  }
  .vedo-auth-modal__title {
    font-size: 18px; font-weight: 600;
    color: var(--white); text-align: center; margin-bottom: 4px;
  }
  .vedo-auth-modal__sub {
    font-size: 12.5px; color: var(--muted);
    text-align: center; margin-bottom: 24px; line-height: 1.5;
  }
  .vedo-auth-tabs {
    display: flex;
    border-bottom: 1px solid var(--teal-border);
    margin-bottom: 22px;
  }
  .vedo-auth-tab {
    flex: 1; background: none; border: none;
    padding: 8px 0 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 500; color: var(--muted);
    cursor: pointer; border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    transition: color var(--transition), border-color var(--transition);
  }
  .vedo-auth-tab.active { color: var(--teal); border-bottom-color: var(--teal); }
  .vedo-auth-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
  .vedo-auth-field label {
    font-size: 11.5px; font-weight: 500;
    color: var(--muted); letter-spacing: 0.06em; text-transform: uppercase;
  }
  .vedo-auth-field input {
    background: var(--teal-dim); border: 1px solid var(--teal-border);
    border-radius: 7px; padding: 10px 13px;
    font-family: 'DM Sans', sans-serif; font-size: 14px; color: var(--white);
    outline: none; transition: border-color var(--transition), background var(--transition);
  }
  .vedo-auth-field input::placeholder { color: var(--muted); }
  .vedo-auth-field input:focus { border-color: var(--teal); background: rgba(0,173,181,0.14); }
  .vedo-auth-error {
    background: rgba(255,87,34,0.1); border: 1px solid rgba(255,87,34,0.3);
    border-radius: 6px; padding: 9px 12px;
    font-size: 12.5px; color: #ff7043; margin-bottom: 14px; line-height: 1.4;
  }
  .vedo-auth-submit {
    width: 100%; background: var(--teal); border: none; border-radius: 7px;
    color: #fff; font-family: 'DM Sans', sans-serif;
    font-size: 14px; font-weight: 600; padding: 11px; cursor: pointer;
    letter-spacing: 0.03em;
    transition: background var(--transition), box-shadow var(--transition), transform var(--transition);
    margin-top: 4px;
  }
  .vedo-auth-submit:hover { background: #009aa2; box-shadow: 0 4px 16px rgba(0,173,181,0.4); transform: translateY(-1px); }
  .vedo-auth-submit:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
  .vedo-auth-divider {
    display: flex; align-items: center; gap: 10px;
    margin: 16px 0; color: var(--muted); font-size: 11px; letter-spacing: 0.1em;
  }
  .vedo-auth-divider::before, .vedo-auth-divider::after {
    content: ''; flex: 1; height: 1px; background: rgba(255,255,255,0.07);
  }
  .vedo-auth-google {
    width: 100%; background: transparent; border: 1px solid rgba(255,255,255,0.12);
    border-radius: 7px; color: var(--white);
    font-family: 'DM Sans', sans-serif; font-size: 13.5px; font-weight: 400;
    padding: 10px; cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 9px;
    transition: background var(--transition), border-color var(--transition);
  }
  .vedo-auth-google:hover { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.22); }
`;

// ─── Cache helpers ─────────────────────────────────────────────────────────────
const getCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) { localStorage.removeItem(CACHE_KEY); return null; }
    return data;
  } catch { return null; }
};

const setCache = (data) => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() })); }
  catch { /* quota cheia */ }
};

// ─── Auth Modal ───────────────────────────────────────────────────────────────
const AuthModal = ({ onClose, onSuccess }) => {
  const [tab, setTab]           = useState('login');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const auth = getAuth();

  const friendlyError = (code) => ({
    'auth/invalid-email':        'E-mail inválido.',
    'auth/user-not-found':       'Usuário não encontrado.',
    'auth/wrong-password':       'Senha incorreta.',
    'auth/email-already-in-use': 'Este e-mail já está cadastrado.',
    'auth/weak-password':        'Senha muito fraca. Use ao menos 6 caracteres.',
    'auth/too-many-requests':    'Muitas tentativas. Tente novamente em breve.',
    'auth/popup-closed-by-user': 'Login cancelado.',
  }[code] || 'Ocorreu um erro. Tente novamente.');

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      if (tab === 'login') await signInWithEmailAndPassword(auth, email, password);
      else                 await createUserWithEmailAndPassword(auth, email, password);
      onSuccess();
    } catch (err) { setError(friendlyError(err.code)); }
    finally       { setLoading(false); }
  };

  const handleGoogle = async () => {
    setError(''); setLoading(true);
    try { await signInWithPopup(auth, new GoogleAuthProvider()); onSuccess(); }
    catch (err) { setError(friendlyError(err.code)); }
    finally     { setLoading(false); }
  };

  return (
    <div className="vedo-auth-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="vedo-auth-modal">
        <button className="vedo-auth-modal__close" onClick={onClose}>×</button>
        <div className="vedo-auth-modal__logo"><span>VEDO</span></div>
        <h2 className="vedo-auth-modal__title">
          {tab === 'login' ? 'Bem-vindo de volta' : 'Criar conta'}
        </h2>
        <p className="vedo-auth-modal__sub">
          {tab === 'login'
            ? 'Entre para visualizar os preços dos domínios.'
            : 'Cadastre-se gratuitamente para ver os preços.'}
        </p>
        <div className="vedo-auth-tabs">
          <button className={`vedo-auth-tab${tab === 'login'    ? ' active' : ''}`} onClick={() => { setTab('login');    setError(''); }}>Entrar</button>
          <button className={`vedo-auth-tab${tab === 'register' ? ' active' : ''}`} onClick={() => { setTab('register'); setError(''); }}>Cadastrar</button>
        </div>
        <form onSubmit={handleEmailAuth}>
          {error && <div className="vedo-auth-error">{error}</div>}
          <div className="vedo-auth-field">
            <label>E-mail</label>
            <input type="email" placeholder="seu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="vedo-auth-field">
            <label>Senha</label>
            <input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <button type="submit" className="vedo-auth-submit" disabled={loading}>
            {loading ? 'Aguarde...' : tab === 'login' ? 'Entrar' : 'Criar conta'}
          </button>
        </form>
        <div className="vedo-auth-divider">ou</div>
        <button className="vedo-auth-google" onClick={handleGoogle} disabled={loading}>
          <svg width="16" height="16" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Continuar com Google
        </button>
      </div>
    </div>
  );
};

// ─── Spinner ──────────────────────────────────────────────────────────────────
const VedoSpinner = ({ label = 'Carregando domínios' }) => (
  <div className="vedo-spinner-wrap">
    <div className="vedo-spinner">
      <div className="vedo-spinner__ring" />
      <div className="vedo-spinner__ring vedo-spinner__ring--mid" />
      <div className="vedo-spinner__ring vedo-spinner__ring--inner" />
      <div className="vedo-spinner__dot" />
    </div>
    <span className="vedo-spinner__label">{label}</span>
  </div>
);

// ─── App ──────────────────────────────────────────────────────────────────────
const App = () => {
  const { dominioNome } = useParams();
  const navigate = useNavigate();

  const [allDominios, setAllDominios]           = useState([]);
  const [setores, setSetores]                   = useState([]);
  const [inputDisplay, setInputDisplay]         = useState('');
  const [filtroBusca, setFiltroBusca]           = useState('');
  const [heroCollapsed, setHeroCollapsed]       = useState(false);
  const [setorSelecionado, setSetorSelecionado] = useState(null);
  const [visibleCount, setVisibleCount]         = useState(BATCH);
  const [loading, setLoading]                   = useState(true);
  const [loadingMore, setLoadingMore]           = useState(false);
  const [filterOpen, setFilterOpen]             = useState(false);
  const [user, setUser]                         = useState(undefined);
  const [showAuthModal, setShowAuthModal]       = useState(false);
  const [revealedPrices, setRevealedPrices]     = useState({});

  const sentinelRef = useRef(null);
  const auth = getAuth();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u ?? null));
    return () => unsub();
  }, []);

  const togglePrice = (id) => {
    if (!user) { setShowAuthModal(true); return; }
    setRevealedPrices((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const cached = getCache();
      if (cached) {
        setAllDominios(shuffle(cached.dominios));
        setSetores(cached.setores);
        setLoading(false);
        return;
      }
      const querySnapshot = await getDocs(collection(firestore, 'dominios'));
      const data = [];
      const setoresSet = new Set();
      querySnapshot.forEach((d) => {
        const item = { ...d.data(), id: d.id };
        if (!item?.nome?.trim() || !item?.setor?.trim()) return;
        // Renderiza somente domínios com status "Publicado"
        if ((item.status || '').trim().toLowerCase() !== 'publicado') return;
        data.push(item);
        setoresSet.add(item.setor);
      });
      const setoresArr = Array.from(setoresSet).sort();
      setCache({ dominios: data, setores: setoresArr });
      setAllDominios(shuffle(data));
      setSetores(setoresArr);
    } catch (err) {
      console.error('Erro ao buscar domínios:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filteredDominios = allDominios.filter((d) => {
    if (setorSelecionado) return d.setor === setorSelecionado;
    if (!filtroBusca) return true;
    return d.nome.toLowerCase().includes(filtroBusca) ||
           d.setor.toLowerCase().includes(filtroBusca);
  });

  const visibleItems = filteredDominios.slice(0, visibleCount);
  const hasMore      = visibleCount < filteredDominios.length;

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    setTimeout(() => { setVisibleCount((c) => c + BATCH); setLoadingMore(false); }, 420);
  }, [hasMore, loadingMore]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const handleFiltroBusca = (value) => {
    setInputDisplay(value);
    setFiltroBusca(value.toLowerCase());
    setSetorSelecionado(null);
    setVisibleCount(BATCH);
    if (value.trim()) setHeroCollapsed(true);
  };

  const handleSetorClick = (setor) => {
    setSetorSelecionado(setor);
    setInputDisplay('');
    setFiltroBusca('');
    setVisibleCount(BATCH);
    setFilterOpen(false);
    if (setor === null) setHeroCollapsed(false);
    else setHeroCollapsed(true);
  };

  const handleConsultarClick = (d) => {
    console.log(`Consultar domínio: ${d.nome}`);
    navigate(`/contato/${d.nome}`);
  };

  const handleReservarClick = async (d) => {
    if (!user) { setShowAuthModal(true); return; }
    try {
      const docRef = doc(firestore, 'dominios', d.id);
      await updateDoc(docRef, { status: 'Reservado' });
      setAllDominios(prev =>
        prev.map(dom => dom.id === d.id ? { ...dom, status: 'Reservado' } : dom)
      );
      console.log(`Reserva do domínio: ${d.nome}`);
      alert(`Domínio ${d.nome} reservado com sucesso!`);
    } catch (error) {
      console.error('Erro ao reservar domínio:', error);
      alert('Erro ao tentar reservar domínio.');
    }
  };

  return (
    <>
      <style>{styles}</style>

      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
          onSuccess={() => setShowAuthModal(false)}
        />
      )}

      <HeroSection
        filtroBusca={inputDisplay}
        onFiltroChange={handleFiltroBusca}
        forceCollapsed={heroCollapsed}
      />

      <div
        className={`vedo-overlay${filterOpen ? ' active' : ''}`}
        onClick={() => setFilterOpen(false)}
      />

      <div className="vedo-home">

        {/* ── Sidebar de filtros ── */}
        <aside className={`vedo-filter${filterOpen ? ' open' : ''}`}>
          <div className="vedo-filter__header">
            <p className="vedo-filter__title">Explorar por setor</p>
            <input
              className="vedo-filter__search"
              type="text"
              placeholder="Buscar domínio..."
              value={inputDisplay}
              onChange={(e) => handleFiltroBusca(e.target.value)}
            />
          </div>

          <div className="vedo-filter__setores">
            <div
              className={`vedo-filter__all${!setorSelecionado && !filtroBusca ? ' active' : ''}`}
              onClick={() => handleSetorClick(null)}
            >
              Todos os domínios
            </div>
            {setores.map((setor) => (
              <div
                key={setor}
                className={`vedo-filter__setor${setorSelecionado === setor ? ' active' : ''}`}
                onClick={() => handleSetorClick(setor)}
              >
                <span className="vedo-filter__setor-name">{setor}</span>
              </div>
            ))}
          </div>
        </aside>

        {/* ── Main ── */}
        <main className="vedo-main">
          <div className="vedo-toolbar">
            <button className="vedo-toolbar__filter-btn" onClick={() => setFilterOpen(true)}>
              <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
                <path d="M1 3h14v1.5l-5 5V14l-4-2V9.5L1 4.5V3z"/>
              </svg>
              Filtros
            </button>
            <div className="vedo-toolbar__info">
              {setorSelecionado
                ? `Explorando: `
                : inputDisplay
                  ? `Resultados para "${inputDisplay}"`
                  : `Explore nosso catálogo`}
            </div>
            {setorSelecionado && (
              <div className="vedo-toolbar__setor-badge" onClick={() => handleSetorClick(null)} title="Remover filtro">
                {setorSelecionado}
                <svg viewBox="0 0 12 12" fill="none">
                  <path d="M8.5 3.5l-5 5M3.5 3.5l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
            )}
          </div>

          {loading ? (
            <VedoSpinner />
          ) : filteredDominios.length === 0 ? (
            <div className="vedo-empty">
              <div className="vedo-empty__icon">🔍</div>
              <p>Nenhum domínio encontrado.</p>
            </div>
          ) : (
            <>
              <div className="vedo-grid">
                {visibleItems.map((dominio, i) => {
                  const status      = dominio.status || 'Disponível';
                  const vendido     = status === 'Vendido';
                  const reservado   = status === 'Reservado';
                  const indisponivel = vendido || reservado;

                  return (
                    <div
                      className={`vedo-card${indisponivel ? ' vedo-card--sold' : ''}`}
                      key={dominio.id}
                      style={{ animationDelay: `${Math.min((i % BATCH) * 18, 280)}ms` }}
                    >
                      {vendido   && <div className="vedo-card__sold-badge">Vendido</div>}
                      {reservado && <div className="vedo-card__sold-badge reserved">Reservado</div>}

                      <div className="vedo-card__domain">{dominio.nome.toLowerCase()}</div>
                      <div className="vedo-card__setor">{dominio.setor}</div>

                      {!indisponivel && dominio.valor && (
                        <div className="vedo-card__price-wrap">
                          {revealedPrices[dominio.id] ? (
                            <div className="vedo-card__price">
                              <span>R$</span>
                              {Number(dominio.valor).toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </div>
                          ) : (
                            <button
                              className="vedo-card__price-toggle"
                              onClick={(e) => { e.stopPropagation(); togglePrice(dominio.id); }}
                              title={user ? 'Ver preço' : 'Faça login para ver o preço'}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                              Ver preço
                            </button>
                          )}
                        </div>
                      )}

                      <div className="vedo-card__spacer" />

                      <button
                        className={`vedo-card__btn${indisponivel ? ' vedo-card__btn--sold' : ''}`}
                        onClick={() => { if (!indisponivel) handleConsultarClick(dominio); }}
                        disabled={indisponivel}
                      >
                        {vendido ? 'Vendido' : reservado ? 'Reservado' : 'Consultar'}
                      </button>
                    </div>
                  );
                })}
              </div>

              <div ref={sentinelRef} className="vedo-sentinel" />

              {loadingMore && (
                <div className="vedo-load-more">
                  <div className="vedo-load-more__ring" />
                  Carregando...
                </div>
              )}

              {!hasMore && filteredDominios.length > 0 && (
                <div className="vedo-end">Fim dos resultados</div>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default App;
